import { type FC, useState } from 'react';
import type { CardInformation } from '@starly/starly-types';

import { desktopBreakpoint } from 'breakpoints';
import type { CardProps } from 'components/Card';
import Card from 'components/Card';
import CardsBundlePopup from 'components/CardsBundlePopup';
import type { TextUnderCardProps } from 'components/TextUnderCard';
import TrimmedCard from 'components/TrimmedCard';
import { useWindowSize } from 'hooks/useWindowSize';

import CollectedCard from '../CollectedCard/CollectedCard';
import { StyledCardCollection, StyledCollectionContainer } from './styled';
import type {
  BundlePopupInfo, CardBundle, CollectedCard as CollectedCardTypeType, CollectionProps,
} from './types';

const renderCardCollection = (
  cards: CollectedCardTypeType[],
  Component: FC<{
    card: CardProps,
    isCollectedCollection?: boolean,
    cardUrl?: string,
    listing?: boolean,
    isActiveCard?: boolean,
    collectionId?: string,
  }>,
  textForCards: TextUnderCardProps[],
  isCollectedCollection?: boolean,
  cardInformation?: CardInformation,
  collectionId?: string,
  creatorUsername?: string,
  ownerUsername?: string,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popupCards, setPopupCards] = useState<BundlePopupInfo>({});
  let filterPredicate: (card: CardProps) => boolean;
  if (isCollectedCollection) {
    filterPredicate = (() => true);
  } else {
    const distinctCommonRareLegendaryCards = cardInformation.distribution.common.distinct_cards
      + cardInformation.distribution.rare.distinct_cards
      + cardInformation.distribution.legendary.distinct_cards;
    filterPredicate = ((card) => Number(card.cardId) <= distinctCommonRareLegendaryCards);
  }

  if (!isCollectedCollection) {
    return cards.filter(filterPredicate)
      .map((card, i) => (
        <CollectedCard
          key={`${collectionId}/${card.cardId}/${card.edition}`}
          card={card}
          Component={Component}
          textForCard={textForCards[i]}
          collectionId={collectionId}
          username={creatorUsername}
          isCollectedCollection={isCollectedCollection}
          dataTestId="card_full_container"
        />
      ));
  }

  const bundle = cards
    .filter(filterPredicate)
    .reduce<CardBundle>((acc, card) => (card.cardId
    ? {
      ...acc,
      [card.cardId]: {
        card: acc[card.cardId] ? acc[card.cardId].card : card,
        amount: acc[card.cardId] ? acc[card.cardId].amount + 1 : 1,
        cards: acc[card.cardId] ? acc[card.cardId].cards.concat([card]) : [card],
        forSaleAmount: card.forSale
          ? (acc[card.cardId]?.forSaleAmount || 0) + 1 : acc[card.cardId]?.forSaleAmount,
        stakedAmount: card.staked
          ? (acc[card.cardId]?.stakedAmount || 0) + 1 : acc[card.cardId]?.stakedAmount,
        lowestPrice:
        // eslint-disable-next-line no-nested-ternary
          acc[card.cardId]?.lowestPrice
            ? card.price
              ? Math.min(acc[card.cardId].lowestPrice!, card.price)
              : acc[card.cardId].lowestPrice
            : card.price,
        staked: acc[card.cardId] ? acc[card.cardId].staked && card.staked : card.staked,
      },
    } : acc), {});

  return (
    <>
      {Object.values(bundle)
        .sort((a, b) => a.card.order - b.card.order)
        .map(({
          card, amount, staked, lowestPrice, cards: bundleCards,
          forSaleAmount,
          stakedAmount,
        }, i) => (
          <CollectedCard
            key={`${collectionId}/${card.cardId}/${card.edition}`}
            card={{
              ...card,
              staked,
              forSale: card.forSale || !!lowestPrice,
              price: lowestPrice,
            }}
            Component={Component}
            textForCard={textForCards[i]}
            collectionId={collectionId}
            username={creatorUsername}
            isCollectedCollection={isCollectedCollection}
            dataTestId="card_full_container"
            bundleAmount={amount}
            onClick={() => {
              setPopupCards({ cards: bundleCards, forSaleAmount, stakedAmount });
              setIsOpen(true);
            }}
          />
        ))}
      {
        isOpen && (
          <CardsBundlePopup
            cards={popupCards.cards}
            forSaleAmount={popupCards.forSaleAmount || 0}
            stakedAmount={popupCards.stakedAmount || 0}
            onClose={() => setIsOpen(false)}
            collectionId={collectionId || ''}
            creatorUsername={creatorUsername || ''}
            ownerUsername={ownerUsername || ''}
          />
        )
      }
    </>
  );
};

const CardCollection: FC<CollectionProps> = ({
  cards,
  cardInformation,
  textForCards,
  isCollectedCollection,
  collectionId,
  creatorUsername,
  ownerUsername,
}) => {
  const width = useWindowSize();
  const isMobile = width < desktopBreakpoint;
  return cards.length > 0 ? (
    <StyledCollectionContainer isCollectedCollection={isCollectedCollection}>
      <StyledCardCollection>
        {renderCardCollection(
          cards,
          isMobile ? TrimmedCard : Card,
          textForCards,
          isCollectedCollection,
          cardInformation,
          collectionId,
          creatorUsername,
          ownerUsername,
        )}
      </StyledCardCollection>
    </StyledCollectionContainer>
  ) : null;
};

export default CardCollection;
